import React, { useState, useEffect, useCallback } from "react";
import { Box, Button, Heading, Text, Spinner, CheckBox } from "grommet";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import BillingService from "../../services/billing.service";
import { presentToastSuccess, presentToastErrorContent } from "@shared/Toast";
import { useTranslation } from "react-i18next";

// Stripe public key for LOXE's billing account
const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_BILLING_PUBLIC_KEY || "",
);

// ─── Card brand icon (text fallback) ─────────────────────────────────────────

const BRAND_LABELS = {
  visa: "Visa",
  mastercard: "Mastercard",
  amex: "American Express",
  discover: "Discover",
  jcb: "JCB",
  unionpay: "UnionPay",
  diners: "Diners Club",
};

function CardBrandLabel({ brand }) {
  return <Text weight="bold">{BRAND_LABELS[brand] || brand || "Card"}</Text>;
}

// ─── Subscription status badge ────────────────────────────────────────────────

const STATUS_COLORS = {
  active: "#00c781",
  trialing: "#00c781",
  past_due: "#ff4040",
  unpaid: "#ff4040",
  canceled: "#999",
  incomplete: "#FFAA15",
  incomplete_expired: "#999",
  // Self-managed renewal statuses
  grace: "#FFAA15",
  suspended: "#ff4040",
  none: "#999",
};

function formatMoney(amount, currency) {
  if (amount == null) return "—";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
      currencyDisplay: "code",
    }).format(amount);
  } catch {
    return `${amount}`;
  }
}

function StatusBadge({ status }) {
  const { t } = useTranslation();
  return (
    <Box
      round="small"
      pad={{ horizontal: "small", vertical: "xsmall" }}
      background={STATUS_COLORS[status] || "#ccc"}
    >
      <Text size="small" color="white">
        {t(`settingsBilling.status.${status}`, { defaultValue: status })}
      </Text>
    </Box>
  );
}

// ─── Payment method update form (inner — needs Stripe context) ─────────────────

function PaymentMethodForm({ stripeCustomerId, onSuccess }) {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const [saving, setSaving] = useState(false);
  const [cardError, setCardError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setSaving(true);
    setCardError(null);

    try {
      // 1. Create a SetupIntent on the server
      const siResponse = await BillingService.createSetupIntent();
      const { clientSecret } = siResponse.data;

      // 2. Confirm the SetupIntent with the card element
      const cardElement = elements.getElement(CardElement);
      const { setupIntent, error } = await stripe.confirmCardSetup(
        clientSecret,
        {
          payment_method: { card: cardElement },
        },
      );

      if (error) {
        setCardError(error.message);
        setSaving(false);
        return;
      }

      // 3. Save the confirmed payment method on the server
      await BillingService.updatePaymentMethod(setupIntent.payment_method);
      presentToastSuccess(t("settingsBilling.paymentMethodUpdated"));
      onSuccess();
    } catch (err) {
      presentToastErrorContent(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box as="form" onSubmit={handleSubmit} gap="small">
      <Box
        border={{ color: "border" }}
        round="small"
        pad="small"
        background="white"
      >
        <CardElement
          options={{
            style: {
              base: {
                fontSize: "16px",
                color: "#333",
                "::placeholder": { color: "#aab7c4" },
              },
            },
          }}
        />
      </Box>
      {cardError && (
        <Text color="status-error" size="small">
          {cardError}
        </Text>
      )}
      <Box direction="row" gap="small">
        <Button
          type="submit"
          primary
          label={
            saving ? t("settingsBilling.saving") : t("settingsBilling.saveCard")
          }
          disabled={saving || !stripe}
        />
        <Button
          label={t("settingsBilling.cancel")}
          onClick={() => onSuccess()}
          disabled={saving}
        />
      </Box>
    </Box>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function SettingsBilling() {
  const { t } = useTranslation();
  const [billing, setBilling] = useState(null);
  const [renewal, setRenewal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUpdateCard, setShowUpdateCard] = useState(false);
  const [autopaySaving, setAutopaySaving] = useState(false);

  const loadBillingInfo = useCallback(async () => {
    setLoading(true);
    try {
      const res = await BillingService.getBillingInfo();
      setBilling(res.data.billing);
      setRenewal(res.data.renewal || null);
    } catch {
      setBilling(null);
      setRenewal(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBillingInfo();
  }, [loadBillingInfo]);

  const handlePaymentMethodSuccess = () => {
    setShowUpdateCard(false);
    loadBillingInfo();
  };

  const toggleAutopay = async (enabled) => {
    setAutopaySaving(true);
    try {
      await BillingService.setAutopay(enabled);
      setRenewal((r) => ({ ...r, autopayEnabled: enabled }));
      presentToastSuccess(t("settingsBilling.autopayUpdated"));
    } catch (err) {
      presentToastErrorContent(err);
    } finally {
      setAutopaySaving(false);
    }
  };

  const payOpenRenewal = async () => {
    try {
      const res = await BillingService.retry();
      if (res.data.hostedInvoiceUrl) {
        window.open(res.data.hostedInvoiceUrl, "_blank");
      } else {
        presentToastErrorContent(t("settingsBilling.noOpenInvoice"));
      }
    } catch (err) {
      presentToastErrorContent(err);
    }
  };

  // Renewal dates are calendar days stored at UTC midnight — format in UTC so a
  // date entered as the 23rd doesn't display as the 22nd in a western timezone.
  const formatRenewalDate = (date) => {
    if (!date) return t("settingsBilling.unknown");
    return new Date(date).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
      timeZone: "UTC",
    });
  };

  // Invoice timestamps are real instants — show them in the viewer's local zone.
  const formatTimestamp = (date) => {
    if (!date) return t("settingsBilling.unknown");
    return new Date(date).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <Box pad="large" align="center">
        <Spinner />
      </Box>
    );
  }

  const status = renewal ? renewal.status : "none";
  // Prefer the live card from the billing customer's Stripe customer (where the
  // billing portal attaches it); fall back to the HotelBilling cache.
  const card = (renewal && renewal.paymentMethod) || billing || {};
  const hasCard = !!card.paymentMethodLast4;

  return (
    // flex={false}: this page's content (subscription + payment method +
    // invoice list) is routinely taller than the dashboard's scrollable
    // content area. Without it, Grommet's default min-height:0 on nested
    // Boxes lets the flex column shrink its children instead of letting the
    // ancestor scroll, which visually collapses/overlaps rows (see bug where
    // renewal date/upcoming amount/autopay rows rendered with 0 height).
    <Box pad="medium" gap="large" width="large" flex={false}>
      <Heading level={3} margin="none">
        {t("settingsBilling.title")}
      </Heading>

      {status === "suspended" && (
        <Box background="#ff4040" pad="small" round="small">
          <Text color="white">{t("settingsBilling.suspendedNotice")}</Text>
        </Box>
      )}
      {status === "grace" && (
        <Box background="#FFAA15" pad="small" round="small">
          <Text>{t("settingsBilling.graceNotice")}</Text>
        </Box>
      )}

      {/* Subscription info */}
      {billing ? (
        <Box gap="medium">
          <Box
            border={{ color: "border" }}
            round="small"
            pad="medium"
            gap="small"
            background="light-1"
          >
            <Text weight="bold">{t("settingsBilling.subscription")}</Text>
            <Box direction="row" align="center" gap="small">
              <Text>{t("settingsBilling.status.label")}:</Text>
              <StatusBadge status={status} />
            </Box>
            {renewal && status !== "none" && (
              <Box gap="small">
                <Box direction="row" gap="small">
                  <Text color="dark-4">
                    {t("settingsBilling.renewalDate")}:
                  </Text>
                  <Text weight="bold">
                    {formatRenewalDate(renewal.renewalDate)}
                  </Text>
                </Box>
                <Box direction="row" gap="small">
                  <Text color="dark-4">
                    {t("settingsBilling.upcomingAmount")}:
                  </Text>
                  <Text weight="bold">
                    {formatMoney(renewal.upcomingTotal, renewal.currency)}
                  </Text>
                </Box>
                <Box
                  direction="row"
                  align="center"
                  gap="small"
                  margin={{ top: "xsmall" }}
                >
                  <Text color="dark-4">{t("settingsBilling.autopay")}:</Text>
                  <CheckBox
                    toggle
                    checked={!!renewal.autopayEnabled}
                    disabled={autopaySaving || !hasCard}
                    onChange={(e) => toggleAutopay(e.target.checked)}
                  />
                </Box>
                {!hasCard && (
                  <Text size="small" color="#FFAA15">
                    {t("settingsBilling.autopayNeedsCard")}
                  </Text>
                )}
                {(status === "grace" || status === "suspended") && (
                  <Button
                    primary
                    size="small"
                    alignSelf="start"
                    margin={{ top: "small" }}
                    label={t("settingsBilling.payNow")}
                    onClick={payOpenRenewal}
                  />
                )}
              </Box>
            )}
          </Box>

          {/* Payment method */}
          <Box
            border={{ color: "border" }}
            round="small"
            pad="medium"
            gap="small"
            background="light-1"
          >
            <Text weight="bold">{t("settingsBilling.paymentMethod")}</Text>
            {card.paymentMethodLast4 ? (
              <Box direction="row" align="center" gap="small">
                <CardBrandLabel brand={card.paymentMethodBrand} />
                <Text>•••• •••• •••• {card.paymentMethodLast4}</Text>
                {card.paymentMethodExpMonth && card.paymentMethodExpYear && (
                  <Text color="dark-5" size="small">
                    {String(card.paymentMethodExpMonth).padStart(2, "0")}/
                    {String(card.paymentMethodExpYear).slice(-2)}
                  </Text>
                )}
              </Box>
            ) : (
              <Text color="dark-4">{t("settingsBilling.noPaymentMethod")}</Text>
            )}

            {!showUpdateCard && (
              <Button
                label={t("settingsBilling.updatePaymentMethod")}
                onClick={() => setShowUpdateCard(true)}
                size="small"
                alignSelf="start"
              />
            )}

            {showUpdateCard && (
              <Elements stripe={stripePromise}>
                <PaymentMethodForm
                  stripeCustomerId={billing.stripeCustomerId}
                  onSuccess={handlePaymentMethodSuccess}
                />
              </Elements>
            )}
          </Box>

          {/* Invoice history */}
          {renewal && renewal.invoices && renewal.invoices.length > 0 && (
            <Box
              border={{ color: "border" }}
              round="small"
              pad="medium"
              gap="small"
              background="light-1"
            >
              <Text weight="bold">{t("settingsBilling.invoices")}</Text>
              {renewal.invoices.map((inv) => (
                <Box
                  key={inv.id}
                  direction="row"
                  justify="between"
                  align="center"
                  gap="medium"
                  border={{ side: "bottom", color: "border" }}
                  pad={{ vertical: "small" }}
                >
                  <Box gap="xxsmall">
                    <Text weight="bold">{inv.invoiceNumber}</Text>
                    <Text size="small" color="dark-4">
                      {formatTimestamp(inv.createdAt)} · {inv.status}
                    </Text>
                  </Box>
                  <Box direction="row" gap="small" align="center" flex={false}>
                    <Text weight="bold">
                      {formatMoney(inv.total, renewal.currency)}
                    </Text>
                    {inv.pdfUrl && inv.status !== "Cancelled" && (
                      <Button
                        size="small"
                        label={t("settingsBilling.download")}
                        onClick={() => window.open(inv.pdfUrl, "_blank")}
                      />
                    )}
                    {inv.hostedInvoiceUrl &&
                      inv.status !== "Paid" &&
                      inv.status !== "Cancelled" && (
                        <Button
                          size="small"
                          primary
                          label={t("settingsBilling.pay")}
                          onClick={() =>
                            window.open(inv.hostedInvoiceUrl, "_blank")
                          }
                        />
                      )}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      ) : (
        <Box pad="medium" background="light-2" round="small">
          <Text color="dark-4">{t("settingsBilling.noBillingInfo")}</Text>
        </Box>
      )}
    </Box>
  );
}
